import React, { useEffect, useState } from 'react';
import styles from './AppointmentTypeZipStep.module.css';
import shared from './SchedulingFlow.module.css';
import telehealthIcon from './SVGs/Device-Imac--Streamline-Tabler.svg';
import inpersonIcon from './SVGs/Building-Community--Streamline-Tabler (1).svg';
import spravatoIcon from './SVGs/Building-Community--Streamline-Tabler (1).svg';
import bgImg from './SVGs/image001.png';
import { AxisBrandLogo, axisBranding } from './branding';

interface AppointmentTypeZipStepProps {
  selectedType: 'telehealth' | 'inperson';
  onSelectType: (type: 'telehealth' | 'inperson') => void;
  zip: string;
  onZipChange: (zip: string) => void;
  radiusMiles: number;
  onRadiusMilesChange: (miles: number) => void;
  onProceed: () => void;
  onBack: () => void;
  isSpravato?: boolean;
}

const AppointmentTypeZipStep: React.FC<AppointmentTypeZipStepProps> = React.memo(({
  selectedType,
  onSelectType,
  zip,
  onZipChange,
  radiusMiles,
  onRadiusMilesChange,
  onProceed,
  onBack,
  isSpravato = false,
}) => {
  // For telehealth, zip/radius are not required and zip input is hidden entirely
  // For Spravato, zip/radius are always required since it's always in-person
  const isTelehealth = selectedType === 'telehealth';
  const canProceed = isTelehealth || ((isSpravato || selectedType === 'inperson') && (/^\d{5}$/.test(zip) && radiusMiles > 0));

  // Shared header/footer content (consistent with other screens)
  const NAV_PHONE = axisBranding.phoneNumber;
  const NAV_LINKS = [
    { label: 'HOME', href: '#', action: 'home' },
  ];
  const NAV_LINKS_MOBILE = [
    { label: 'HOME', href: '#', action: 'home' },
  ];
  const [isMobile, setIsMobile] = useState(false);
  
  const checkMobile = React.useCallback(() => {
    setIsMobile(window.innerWidth <= 700);
  }, []);

  useEffect(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [checkMobile]);
  const currentNavLinks = isMobile ? NAV_LINKS_MOBILE : NAV_LINKS;

  const handleNavClick = (action: string) => {
    if (action === 'home') {
      // Refresh the page to go back to landing
      window.location.reload();
    }
  };

  return (
    <div className={shared.landingRoot}>
      {/* Top Banner with shared nav/header */}
      <div className={shared.topBanner}>
        <img src={bgImg} alt="Banner" className={shared.bannerImg} />
        <div className={shared.bannerOverlay} />
        <div className={shared.navBar}>
          <AxisBrandLogo
            variant="white"
            size="small"
            className={shared.navLogo}
            onClick={() => window.open(axisBranding.websiteUrl, '_blank')}
            style={{ cursor: 'pointer' }}
          />
          <span className={shared.navPhone}>{NAV_PHONE}</span>
          <div className={shared.navLinks}>
            {currentNavLinks.map(link => (
              <button key={link.label} className={shared.navLink} onClick={() => handleNavClick(link.action)}>{link.label}</button>
            ))}
          </div>
        </div>
        <div className={shared.bannerContent}>
          <h1 className={shared.bannerTitle}>Book Online</h1>
          <div className={shared.bannerSubtitle}>
            {isSpravato 
              ? "Schedule your Spravato treatment appointment with our qualified providers."
              : "Schedule your mental health appointment with our qualified providers. Choose between telehealth or in-person visits."
            }
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={shared.landingMain} style={{ marginTop: 0 }}>
        <div className={styles.textParent}>
        <div className={`${styles.cards} ${isSpravato ? styles.singleCard : ''}`}>
          {!isSpravato ? (
            <>
              <div
                className={`${styles.card} ${selectedType === 'telehealth' ? styles.selected : ''}`}
                onClick={() => onSelectType('telehealth')}
              >
                <div className={styles.cardIcon}><img src={telehealthIcon} alt="Telehealth" /></div>
                <div className={styles.cardTitle}>Telehealth</div>
                <div className={styles.cardDesc}>Meet with your provider from the comfort of your home (Colorado residents only)</div>
              </div>
              <div
                className={`${styles.card} ${selectedType === 'inperson' ? styles.selected : ''}`}
                onClick={() => onSelectType('inperson')}
              >
                <div className={styles.cardIcon}><img src={inpersonIcon} alt="In-person" /></div>
                <div className={styles.cardTitle}>In-person</div>
                <div className={styles.cardDesc}>Visit our office for face-to-face consultation</div>
              </div>
            </>
          ) : (
            <div
              className={`${styles.card} ${styles.selected}`}
            >
              <div className={styles.cardIcon}><img src={spravatoIcon} alt="Spravato" /></div>
              <div className={styles.cardTitle}>Spravato Appointment</div>
            </div>
          )}
        </div>

        {isTelehealth ? (
          <div className={styles.infoBox}>
            Telehealth appointments are available to Colorado residents only.
          </div>
        ) : (isSpravato || selectedType === 'inperson') ? (
          <div className={styles.locationBox}>
            <div className={styles.locationTitle}>Location Information</div>
            <div className={styles.locationDesc}>
              Please provide your zip code and search radius to find nearby providers
            </div>
            <div className={styles.inputRow}>
              <div className={styles.inputField}>
                <label className={styles.inputLabel} htmlFor="zip">
                  Zip Code *
                </label>
                <input
                  className={styles.input}
                  id="zip"
                  type="text"
                  value={zip}
                  onChange={e => onZipChange(e.target.value)}
                  maxLength={5}
                  pattern="\d{5}"
                  placeholder="Enter your zip code"
                  autoComplete="postal-code"
                />
              </div>
              <div className={styles.inputField}>
                <label className={styles.inputLabel} htmlFor="radius">
                  Search Within (Miles) *
                </label>
                <input
                  className={styles.input}
                  id="radius"
                  type="number"
                  min="1"
                  max="100"
                  value={radiusMiles || ''}
                  onChange={e => onRadiusMilesChange(parseInt(e.target.value) || 0)}
                  placeholder="Enter radius"
                />
              </div>
            </div>
          </div>
        ) : null}

          <button
            className={`${styles.button} ${canProceed ? styles.enabled : ''}`}
            disabled={!canProceed}
            onClick={canProceed ? onProceed : undefined}
          >
            Proceed to Provider Selection
          </button>
          <button style={{marginTop: 12}} onClick={onBack}>Back</button>
        </div>
      </div>

      {/* Footer */}
      <footer className={shared.landingFooter}>
        <div className={shared.footerContent}>
          <div className={shared.footerTitle}>{axisBranding.organizationName}</div>
          <div className={shared.footerRating}>
            <span className={shared.footerStars}>★★★★★</span>
            <span className={shared.footerAvg}>Average Rating: 4.9/5</span>
          </div>
        </div>
      </footer>
    </div>
  );
});

export default AppointmentTypeZipStep; 